import { titleCasedSlug } from "../../boundaries/slug.parser";
import type { EstateLabelPlacement } from "../../enums/scan";
import { ESTATE_LABEL_PLACEMENT } from "../../enums/scan";
import { inferredClusterInitialism } from "./boundaries/cluster-label.parser";
import {
  type EstateLayout,
  type EstateNode,
  type EstateViewport,
  PAN_BOUNDS_PADDING,
  TOOLTIP_EDGE_MARGIN,
  TOOLTIP_HEIGHT,
  TOOLTIP_MARGIN,
  TOOLTIP_WIDTH,
  type ViewTransform,
} from "./estateTypes";

export function bringNodeIntoView(
  current: ViewTransform,
  node: EstateNode,
  layout: EstateLayout,
): ViewTransform {
  const margin = 9;
  const screenX = current.x + node.x * current.scale;
  const screenY = current.y + node.y * current.scale;
  let x = current.x;
  let y = current.y;
  if (screenX < margin) {
    x += margin - screenX;
  } else if (screenX > layout.viewport.width - margin) {
    x -= screenX - (layout.viewport.width - margin);
  }
  if (screenY < margin) {
    y += margin - screenY;
  } else if (screenY > layout.viewport.height - margin) {
    y -= screenY - (layout.viewport.height - margin);
  }
  return clampView({ ...current, x, y }, layout);
}

export function clampView(
  view: ViewTransform,
  layout: EstateLayout,
): ViewTransform {
  const contentWidth = layout.width * view.scale;
  const contentHeight = layout.height * view.scale;
  const x =
    contentWidth <= layout.viewport.width
      ? (layout.viewport.width - contentWidth) / 2
      : clamp(
          view.x,
          layout.viewport.width - contentWidth - PAN_BOUNDS_PADDING,
          PAN_BOUNDS_PADDING,
        );
  const y =
    contentHeight <= layout.viewport.height
      ? (layout.viewport.height - contentHeight) / 2
      : clamp(
          view.y,
          layout.viewport.height - contentHeight - PAN_BOUNDS_PADDING,
          PAN_BOUNDS_PADDING,
        );
  return { ...view, x, y };
}

export function clusterSort(left: string, right: string): number {
  return (
    clusterSortWeight(left) - clusterSortWeight(right) ||
    left.localeCompare(right)
  );
}

function clusterSortWeight(key: string): number {
  switch (key) {
    case "home":
      return 0;
    case "collections":
      return 1;
    case "products":
      return 2;
    case "guides":
      return 3;
    case "policies":
      return 4;
    case "support":
      return 5;
    case "checkout":
      return 6;
    default:
      return 7;
  }
}

type ClusterLabelEvidence = {
  path: string;
  title?: string | null;
};

export function clusterLabel(
  key: string,
  pages: readonly ClusterLabelEvidence[] = [],
): string {
  if (key === "home") {
    return "Home";
  }
  const initialism = inferredClusterInitialism(key, pages);
  if (initialism) return initialism;
  return titleCasedSlug(key);
}

export function estateTooltipPosition(
  node: EstateNode,
  mapElement: SVGSVGElement | undefined,
  view: ViewTransform | null,
  viewport: EstateViewport,
): {
  left: number;
  top: number;
  width: number;
  placement: EstateLabelPlacement;
} {
  if (!mapElement) {
    return {
      left: TOOLTIP_EDGE_MARGIN,
      top: TOOLTIP_EDGE_MARGIN,
      width: TOOLTIP_WIDTH,
      placement: ESTATE_LABEL_PLACEMENT.ABOVE,
    };
  }
  const rect = mapElement.getBoundingClientRect();
  const viewportX = view ? view.x + node.x * view.scale : node.x;
  const viewportY = view ? view.y + node.y * view.scale : node.y;
  const nodeLeft = rect.left + (viewportX / viewport.width) * rect.width;
  const nodeTop = rect.top + (viewportY / viewport.height) * rect.height;
  const availableWidth = Math.max(
    160,
    window.innerWidth - TOOLTIP_EDGE_MARGIN * 2,
  );
  const width = Math.min(TOOLTIP_WIDTH, availableWidth);
  const maxLeft = Math.max(
    TOOLTIP_EDGE_MARGIN,
    window.innerWidth - width - TOOLTIP_EDGE_MARGIN,
  );
  const aboveTop = nodeTop - TOOLTIP_HEIGHT - TOOLTIP_MARGIN;
  const belowTop = nodeTop + TOOLTIP_MARGIN;
  const minTop = Math.max(TOOLTIP_EDGE_MARGIN, rect.top + TOOLTIP_EDGE_MARGIN);
  const maxTop = Math.max(
    minTop,
    Math.min(
      window.innerHeight - TOOLTIP_HEIGHT - TOOLTIP_EDGE_MARGIN,
      rect.bottom - TOOLTIP_HEIGHT - TOOLTIP_EDGE_MARGIN,
    ),
  );
  const placement = aboveTop >= minTop ? "above" : "below";

  return {
    left: clamp(nodeLeft - width / 2, TOOLTIP_EDGE_MARGIN, maxLeft),
    top: clamp(
      placement === ESTATE_LABEL_PLACEMENT.ABOVE ? aboveTop : belowTop,
      minTop,
      maxTop,
    ),
    width,
    placement,
  };
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
